import React from 'react';

import { GenericErrorBoundary } from './services/generic-error-boundary';
import type { UniversalCreateButtonCombinedProps } from './types/entrypoint';
import { UniversalCreateButtonLazy } from './ui/components/LazyTrigger/LazyTrigger';

/**
 * A universal "Create" button used across Atlassian products to provide a consistent
 * content-creation experience. It renders a trigger button that, when clicked, opens a
 * popup menu listing creation options (e.g. pages, issues, goals) sourced from the
 * host product and optionally from cross-app integrations.
 *
 * Pass structured `MenuSectionV2[]` through the `sections` prop to populate the menu.
 * Optionally enable cross-app creation via `crossAppCreationConfig`.
 *
 * > **Migration note:** The V1 `items` prop has been removed — use `sections` instead.
 * > The V1 `suggestionConfig` prop has been removed — use `crossAppCreationConfig` instead.
 *
 * Setting `buttonOnly: true` renders just the trigger button without
 * the popup menu, which is useful for scenarios like Jira's global issue create.
 *
 * The component wraps its content with:
 * - An error boundary (`GenericErrorBoundary`) for resilient rendering.
 * - A UFO performance tracking segment for monitoring component load times.
 * - Relay and BFF (Backend-for-Frontend) data providers for remote data fetching.
 * - An analytics wrapper for instrumented event tracking.
 * - A flags provider for in-context messaging (e.g. success/error toasts).
 *
 * @example with Menu items
 * ```tsx
 * import { UniversalCreateButton } from '@atlassian/universal-create';
 * import type { UniversalCreateMenuSection } from '@atlassian/universal-create/types';
 *
 * const sections: UniversalCreateMenuSection[] = [
 *   { title: 'Confluence', items: [...] },
 *   { title: 'Jira', items: [...] },
 * ];
 *
 * <UniversalCreateButton product="confluence" sections={sections}>
 *   Create
 * </UniversalCreateButton>
 * ```
 *
 * @example Button-only mode (no popup)
 * ```tsx
 * <UniversalCreateButton product="jira" buttonOnly onClick={handleClick}>
 *   Create
 * </UniversalCreateButton>
 * ```
 *
 * @example With cross-app creation config
 * ```tsx
 *   const crossAppConfig: CrossAppCreationConfig = useMemo(
 *     () => ({
 *       enabled: true,
 *       prefetch: true // this enables hover to fetch API, otherwise API is fetch upon click
 *       cloudId,
 *       orgId,
 *       accountId,
 *     }),
 *     [cloudId, orgId, accountId],
 *   );
 *
 *   const sections = useMemo(
 *     () => [
 *       {
 *         type: 'appDefinedSection',
 *         items: [
 *           { ati: Ati.parse('ati:cloud:confluence:page'), children: 'Page', onClick: handleCreatePage },
 *           { ati: Ati.parse('ati:cloud:jira:issue'), type: 'link' },
 *         ],
 *       },
 *     ],
 *     [],
 *   );
 *
 *   return (
 *     <UniversalCreateButton
 *       product="confluence"
 *       sections={sections}
 *       crossAppCreationConfig={crossAppConfig}
 *     >
 *       Create
 *     </UniversalCreateButton>
 *   );
 * ```
 *
 * The `crossAppCreationConfig` prop accepts a {@link CrossAppCreationConfig} object which is a
 * discriminated union on the `enabled` field:
 * - When `enabled: true`, provide `cloudId`, `orgId`, and `accountId` so the component can
 *   call the V2 backend to hydrate cross-app items (e.g. Jira issues from within Confluence).
 *   Optional fields include `environment`, `prefetch`, `crossAppLinkOpenTarget`, and `onItemClick`.
 * - When `enabled: false`, no additional fields are required and cross-app items render statically.
 *
 * @param props - The component props. See {@link UniversalCreateButtonPropsV2} for the full definition.
 * @param props.product - The name of the consuming Atlassian product (e.g. `'confluence'`, `'jira'`).
 * @param props.children - The button label text (should be an i18n-translated string such as `'Create'`).
 * @param props.sections - Structured menu sections to display in the creation popup.
 * @param props.buttonOnly - When `true`, renders only the trigger button without popup content.
 * @param props.onClick - Optional callback invoked when the trigger button is clicked.
 * @param props.onClose - Optional callback invoked when the popup menu is closed.
 * @param props.isOpen - Optional controlled open state for the popup (mirrors `@atlaskit/popup`).
 * @param props.primaryActionItem - When provided, enables the split-button variant with a primary action.
 * @param props.crossAppCreationConfig - Configuration for cross-app creation. See {@link CrossAppCreationConfig}.
 * @param props.testId - Optional test ID passed to the underlying popup (mirrors `@atlaskit/popup`).
 *
 * @see {@link UniversalCreateButtonPropsV2} for the full props definition.
 * @see {@link CrossAppCreationConfig} for the cross-app creation config shape.
 */
export function UniversalCreateButton(props: UniversalCreateButtonCombinedProps): JSX.Element {
	return (
		<GenericErrorBoundary name="universal-create-button-lazy" app={props.product}>
			<UniversalCreateButtonLazy {...props} />
		</GenericErrorBoundary>
	);
}
